import { memo, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { CareGetBehaviorDataResult } from '../../../serverApi';
import { extent, scaleUtc } from 'd3';

import { useWindowSize } from '../../../lib/useWindowSize';

import { Icon, IconDefinition } from '../../../components/Icon';
import BooleanSeriesItem from './BooleanSeriesItem';
import { AreaChart } from './AreaChart';
import { BehaviorChartsXAxis } from './BehaviorChartsXAxis';
import { HighlightedAreaAndVerticalRuler } from './HighlightedAreaAndVerticalRuler';
import { PausedSensorOverlay } from './PausedSensorOverlay';
import { useD3Zoom } from './useD3Zoom';
import useSize from './useSize';
import { processBooleanSeriesData } from './helpers/processBooleanSeriesData';
import { processBehaviorAreaChartData } from './helpers/processBehaviorAreaChartData';
import { RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { t } from '../../../lib/i18n';

import './BehavioralDataChart.scss';

/**
 * Chart constants
 */
const height = 240;
const margin = { top: 20, right: 0, bottom: 30, left: 47 };

/**
 * Helper function to get the correct values for chart x axis
 */
const renderXValue = (d: any) => d.date;
const renderYValue = (d: any, key: string) => d[key];

const labelIcon: Record<string, IconDefinition> = {
  upInBed: 'bed-upright',
  inBed: 'bed-sleeping',
  inChair: 'chair-sitting',
  inBathroom: 'door-heart',
  roomActivity: 'activity',
};

interface Props {
  data: CareGetBehaviorDataResult;
  showSoundDimension?: boolean;
}

type BehaviorChartDimensionType =
  | 'bedActivity'
  | 'audioLevel'
  | 'bedActivityAndAudioLevel';

export const BehavioralDataChart = memo(
  ({ data, showSoundDimension }: Props) => {
    const [selectedDimension, setSelectedDimension] =
      useState<BehaviorChartDimensionType>(() =>
        showSoundDimension ? 'bedActivityAndAudioLevel' : 'bedActivity'
      );

    const boundingDivRef = useRef<HTMLDivElement>(null);
    const windowSize = useWindowSize();
    const size = useSize(boundingDivRef);

    const width = size?.width || 1;
    const boundingDivHeight = size?.height || 1;

    const [zoomSelection, setZoomSelection] = useState<[Date, Date]>(); //Chart x-axis domain with zoom applied

    const processAudioLevels =
      selectedDimension === 'audioLevel' ||
      selectedDimension === 'bedActivityAndAudioLevel';

    const chartData = useMemo(
      () => processBehaviorAreaChartData(data, processAudioLevels),
      [data, processAudioLevels]
    );

    const booleanData = useMemo(() => {
      const processedData = processBooleanSeriesData(data);
      const orderedLabels = Object.keys(labelIcon);

      const seriesBooleanData = orderedLabels.map((label) => {
        return processedData.find((item) => item.label === label);
      });

      const pausedSensorBooleanData = processedData.find(
        (item) => item.label === 'pauseSensor'
      );

      return {
        seriesBooleanData,
        pausedSensorBooleanData,
      };
    }, [data]);

    const marginLeft = windowSize.isMobile ? 35 : 47; //Used to offset the brush and area chart according to boolean series chart

    /**
     * Apply d3 zoom to the behavioral data bounding div
     */
    useD3Zoom(boundingDivRef, handleZoom, {
      scaleExtent: [1, 100],
      translateExtent: [
        [marginLeft, 0],
        [width, height - margin.bottom - margin.top],
      ],
      extent: [
        [marginLeft, 0],
        [width, height - margin.top - margin.bottom],
      ],
    });

    /**
     * Current zoom extent that's applied to charts
     */
    const zoomScale = boundingDivRef.current
      ? d3.zoomTransform(boundingDivRef.current).k
      : 1;

    /**
     * xScale domain with selection applied from zoom or brush (defaults to full chart domain)
     */
    const xDomainWithSelection: any = zoomSelection
      ? zoomSelection
      : extent(chartData, renderXValue);

    /**
     * Define memoized x-scale with domain based on the chart selection
     */
    const xScaleWithSelection = useMemo(() => {
      return scaleUtc()
        .domain(xDomainWithSelection)
        .range([marginLeft, width - margin.right]);
    }, [width, xDomainWithSelection, marginLeft]);

    /**
     * Define memoized x-scale only with the whole domain
     * This is used to calculate the x-scale domain while zooming in area chart
     */
    const fullXScale = useMemo(() => {
      return scaleUtc()
        .domain(extent(chartData, renderXValue) as any)
        .range([marginLeft, width - margin.right]);
    }, [width, chartData, marginLeft]);

    /**
     * Handler for behavioral data charts zoom event;
     */
    function handleZoom(event: any) {
      const newDomain = event.transform.rescaleX(fullXScale).domain();
      setZoomSelection(newDomain);
    }

    const areaChartColors = {
      bedActivity: 'rgb(132, 185, 60)',
      audioLevel: `rgba(98, 181, 255 ,${
        selectedDimension === 'audioLevel' ? 1 : 0.5
      })`,
    };

    const areaChartKeys =
      selectedDimension === 'bedActivityAndAudioLevel'
        ? ['bedActivity', 'audioLevel']
        : [selectedDimension];

    return (
      <>
        {showSoundDimension && (
          <RadioGroup
            name="max_call_duration_oneway_audio"
            value={selectedDimension}
            onChange={(e) =>
              setSelectedDimension(e.target.value as BehaviorChartDimensionType)
            }
            style={{ marginBottom: '14px' }}
          >
            <FormControlLabel
              value="bedActivityAndAudioLevel"
              control={<Radio color="primary" style={{ padding: '4px' }} />}
              label={t(
                'care.components.BehaviorPanel.bedActivityAndAudioLevel'
              )}
            />
            <FormControlLabel
              value="bedActivity"
              control={<Radio color="primary" style={{ padding: '4px' }} />}
              label={t('care.components.BehaviorPanel.bedActivity')}
            />
            <FormControlLabel
              value="audioLevel"
              control={<Radio color="primary" style={{ padding: '4px' }} />}
              label={t('care.components.BehaviorPanel.audioLevel')}
            />
          </RadioGroup>
        )}
        <div className="BehavioralDataChart" ref={boundingDivRef}>
          <AreaChart
            keys={areaChartKeys}
            colors={areaChartColors}
            data={chartData}
            xScale={xScaleWithSelection}
            xValue={renderXValue}
            yValue={renderYValue}
            yIcon={
              selectedDimension === 'bedActivity'
                ? bedActivityIcon
                : selectedDimension === 'audioLevel'
                ? audioLevelIcon
                : undefined
            }
            settings={{
              width,
              height,
              margin: { ...margin, left: marginLeft },
            }}
          />

          <div className="BehavioralDataChart__boolean-series">
            <div className="BehavioralDataChart__boolean-icons">
              {booleanData.seriesBooleanData.map((item) => {
                if (item === undefined) {
                  return null;
                }
                return (
                  <Icon
                    key={item.label}
                    className="BehavioralDataChart__boolean-series-icon"
                    icon={labelIcon[item.label]}
                  />
                );
              })}
            </div>

            <div className="BehavioralDataChart__boolean-items">
              {booleanData.seriesBooleanData.map((item) => {
                if (item === undefined) {
                  return null;
                }
                return (
                  <BooleanSeriesItem
                    key={item.label}
                    values={item.values}
                    domain={xDomainWithSelection}
                    samplePeriodS={data.time.samplePeriodS}
                    width={width - margin.left}
                  />
                );
              })}
            </div>
          </div>

          <BehaviorChartsXAxis
            xScale={xScaleWithSelection}
            width={width}
            zoomScale={zoomScale}
          />
          <HighlightedAreaAndVerticalRuler
            xScale={xScaleWithSelection}
            width={width}
            height={boundingDivHeight}
            margin={{ ...margin, left: marginLeft }}
            startTime={data.time.startTime}
          />
          <PausedSensorOverlay
            values={
              booleanData.pausedSensorBooleanData
                ? booleanData.pausedSensorBooleanData.values
                : []
            }
            xScale={xScaleWithSelection}
            width={width}
            height={boundingDivHeight}
            margin={{ ...margin, left: marginLeft }}
            samplePeriodS={data.time.samplePeriodS}
          />
          {/* <AreaBrush
        data={chartData}
        settings={{ width: calculatedWidth, height: focusHeight, margin }}
        onBrush={handleBrushandZoom}
        xValue={renderXValue}
        yValue={renderYValue}
        tickFormatter={formatBrushXAxisTickLabels}
        style={{ marginLeft }}
        selectedDomain={selection}
        previous={previousSelection}
      /> */}
        </div>
      </>
    );
  }
);

const bedActivityIcon = `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
viewBox="0 0 1250 1250" style="enable-background:new 0 0 1250 1250;" xml:space="preserve" fill="#424549">
<path d="M607.2,349.5l29-29v85.4c0,10.4,8.7,19.2,19.1,19.2c10.4,0,19.2-8.7,19.2-19.2v-85.4l29,29c7,7,19.7,7,26.7,0
c3.5-3.5,5.8-8.7,5.8-13.9c0-5.2-2.3-10.4-5.8-13.9l-61.5-62.1c-7-7-18.6-7-26.1-0.6l-62.7,62.7c-3.5,3.5-5.8,8.7-5.8,13.9
c0,5.2,2.3,10.4,6.4,13.9C587.4,356.5,600.2,356.5,607.2,349.5z"/>
<path d="M820.7,464.4h-85.4c-10.4,0-19.2,8.7-19.2,19.1c0,10.4,8.7,19.2,19.2,19.2h85.4l-29,29c-7,7-7,19.7,0,26.7
c3.5,3.5,8.7,5.8,13.9,5.8c5.2,0,10.4-2.3,13.9-5.8l62.1-61.5c7-7,7-18.6,0.6-26.1l-62.7-62.7c-3.5-3.5-8.7-5.8-13.9-5.8
c-5.2,0-10.4,2.3-13.9,6.4c-7,7-7,19.7,0,26.7L820.7,464.4z"/>
<path d="M490.5,558.4c3.5,3.5,8.7,5.8,13.9,5.8c5.2,0,10.4-2.3,13.9-6.4c7-7,7-19.7,0-26.7l-29-29h85.4c10.4,0,19.2-8.7,19.2-19.1
c0-10.4-8.7-19.2-19.2-19.2h-85.4l29-29c7-7,7-19.7,0-26.7c-3.5-3.5-8.7-5.8-13.9-5.8c-5.2,0-10.4,2.3-13.9,5.8l-62.1,61.5
c-7,7-7,18.6-0.6,26.1L490.5,558.4z"/>
<path d="M619.8,483.3c0,19.5,15.8,35.2,35.2,35.2c19.5,0,35.2-15.8,35.2-35.2s-15.8-35.2-35.2-35.2
C635.5,448.1,619.8,463.8,619.8,483.3z"/>
<path d="M1099.7,850.4c12.9-75.7,18.6-108.7-4.3-115.8c-32.9-10-75.7,37.1-104.3,124.4c-85.7-14.3-210.1-24.3-347.3-24.3
c-123,0-245.8,7.1-330.2,20c-5.7-47.1-11.4-108.7-32.9-168.7c-51.4-141.5-112.9-168.6-141.5-154.4c-25.7,12.9,50,134.4,57.1,294.4
c4.3,102.9-28.6,264.4,8.6,271.5c30,5.7,64.3-41.4,87.1-111.5c85.7,15.7,211.5,24.3,351.6,24.3c143,0,270.1-10,355.9-25.7
c22.9,70,57.1,117.2,87.1,111.5c30-5.7,20-54.3,10-128.7C1094,951.9,1091.1,903.4,1099.7,850.4z"/>
<path d="M321.7,690.5c15.7,47.1,20.5,87.1,27.6,127.2c75.7-10,175.8-17.1,281.5-17.1c131.5,0,250.1,10,330.2,25.7
c4.3-5.7,7.1-17.1,5.7-25.7c-15.7-111.6-225.8-175.9-383-167.3c-58.6,2.9-65.7,37.1-81.4,60C486.6,716.2,463.7,729,448,729
c-5.7,0-10-7.1-5.7-11.4c7.1-5.7,12.9-10,17.1-21.4c12.9-34.3-8.6-72.9-47.1-87.1c-37.1-12.9-78.6,2.9-91.4,35.7
c-5.7,14.3-4.3,28.6,0,42.9C321.3,689.7,321.7,690.5,321.7,690.5z"/>
</svg>`;

const audioLevelIcon = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="-125 -125 1250 1250" version="1.1">
<g>
<path d="M 592.855469 115.714844 C 581.429688 94.285156 558.570312 78.570312 534.285156 77.144531 C 508.570312 74.285156 484.285156 85.714844 468.570312 104.285156 L 251.429688 370 L 147.144531 398.570312 C 112.855469 408.570312 82.855469 431.429688 65.714844 462.855469 C 48.570312 494.285156 44.285156 531.429688 52.855469 565.714844 C 62.855469 600 85.714844 630 117.144531 647.144531 C 137.144531 658.570312 158.570312 664.285156 180 664.285156 C 234.285156 880 285.714844 928.570312 321.429688 928.570312 C 324.285156 928.570312 325.714844 928.570312 328.570312 928.570312 C 337.144531 927.144531 350 922.855469 360 908.570312 C 392.855469 862.855469 375.714844 721.429688 364.285156 647.144531 L 650 755.714844 C 658.570312 758.570312 668.570312 760 677.144531 760 C 692.855469 760 708.570312 755.714844 721.429688 745.714844 C 741.429688 731.429688 754.285156 707.144531 752.855469 681.429688 C 748.570312 590 731.429688 488.570312 702.855469 388.570312 C 674.285156 290 635.714844 195.714844 592.855469 115.714844 Z M 115.714844 550 C 110 531.429688 112.855469 512.855469 121.429688 495.714844 C 131.429688 478.570312 145.714844 467.144531 164.285156 461.429688 L 247.144531 438.570312 L 285.714844 575.714844 L 202.855469 598.570312 C 184.285156 604.285156 165.714844 601.429688 148.570312 592.855469 C 132.855469 582.855469 121.429688 567.144531 115.714844 550 Z M 311.429688 842.855469 C 294.285156 812.855469 270 751.429688 244.285156 654.285156 L 294.285156 640 C 312.855469 740 315.714844 807.144531 311.429688 842.855469 Z M 681.429688 692.855469 C 680 694.285156 675.714844 695.714844 671.429688 694.285156 L 352.855469 574.285156 L 305.714844 407.144531 L 517.144531 147.144531 C 520 142.855469 524.285156 142.855469 527.144531 142.855469 C 528.570312 142.855469 532.855469 144.285156 535.714844 148.570312 C 577.144531 224.285156 612.855469 312.855469 638.570312 407.144531 C 665.714844 502.855469 681.429688 598.570312 685.714844 684.285156 C 685.714844 688.570312 682.855469 691.429688 681.429688 692.855469 Z M 681.429688 692.855469 "/>
<path d="M 907.144531 357.144531 C 924.285156 352.855469 934.285156 334.285156 930 317.144531 C 925.714844 300 907.144531 290 890 294.285156 L 760 331.429688 C 742.855469 335.714844 732.855469 354.285156 737.144531 371.429688 C 741.429688 385.714844 754.285156 395.714844 768.570312 395.714844 C 771.429688 395.714844 774.285156 395.714844 777.144531 394.285156 Z M 907.144531 357.144531 "/>
<path d="M 724.285156 252.855469 C 731.429688 252.855469 738.570312 250 745.714844 245.714844 L 822.855469 178.570312 C 835.714844 167.144531 838.570312 147.144531 825.714844 132.855469 C 814.285156 120 794.285156 117.144531 780 130 L 702.855469 197.144531 C 690 208.570312 687.144531 228.570312 700 242.855469 C 705.714844 250 715.714844 252.855469 724.285156 252.855469 Z M 724.285156 252.855469 "/>
<path d="M 911.429688 491.429688 L 810 474.285156 C 792.855469 471.429688 775.714844 482.855469 772.855469 501.429688 C 770 520 781.429688 535.714844 800 538.570312 L 901.429688 555.714844 C 902.855469 555.714844 904.285156 555.714844 907.144531 555.714844 C 922.855469 555.714844 937.144531 544.285156 938.570312 528.570312 C 941.429688 511.429688 928.570312 494.285156 911.429688 491.429688 Z M 911.429688 491.429688 "/>
</g>
</svg>`;
